import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNotice } from '../../context/NoticeContext';
import { useTeamContext } from '../../context/TeamContext';
import { useDashboard } from '../../context/DashboardContext';
import { useI18n } from '../../i18n';
import NoticeCard from './NoticeCard';
import CreateNoticeModal from './CreateNoticeModal';
import ViewNoticeModal from './ViewNoticeModal';
import NoticeCardSkeleton from './NoticeCardSkeleton';
import DeleteNoticeModal from './DeleteNoticeModal';
import { FaBullhorn } from 'react-icons/fa';
import { ReactComponent as PlusIcon } from '../../assets/icons/plus-icon.svg';
import { notify } from '../CustomToast';
import './NoticeboardSection.css';
import BackButton from '../Common/BackButton';

const NoticeboardSection = () => {
    const { t } = useI18n();
    const {
        notices,
        loading,
        loadingMore,
        hasMore,
        currentPage,
        fetchNotices,
        removeNotice,
        activeTeamId
    } = useNotice();
    const { teams } = useTeamContext();
    const { setActiveView } = useDashboard();

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editNotice, setEditNotice] = useState(null);
    const [selectedNotice, setSelectedNotice] = useState(null);
    const [deleteNotice, setDeleteNotice] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const observer = useRef();

    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    const currentUserId = user?.id;

    const activeTeam = teams.find(t => t.id === activeTeamId);
    const isOwner = Boolean(activeTeam?.permissions?.is_owner);

    const handleDeleteConfirm = async () => {
        if (!deleteNotice) return;
        setDeleteLoading(true);
        try {
            await removeNotice(deleteNotice.id);
            notify('success', t('common.status.success'));
            closeDeleteModal();
        } catch (err) {
            notify('error', err.message || t('common.errors.failed_action'));
        } finally {
            setDeleteLoading(false);
        }
    };

    const closeDeleteModal = () => {
        setDeleteNotice(null);
    };

    const handleBack = () => {
        setActiveView('teams');
    };



    // Infinite scroll observer
    const lastNoticeElementRef = useCallback(node => {
        if (loading || loadingMore) return;
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                fetchNotices(activeTeamId, currentPage + 1, true);
            }
        });

        if (node) observer.current.observe(node);
    }, [loading, loadingMore, hasMore, fetchNotices, activeTeamId, currentPage]);

    useEffect(() => {
        if (activeTeamId) {
            fetchNotices(activeTeamId, 1);
        }
    }, [activeTeamId, fetchNotices]);


    if (!activeTeamId) {
        return (
            <div className="noticeboard-section empty">
                <div className="empty-state">
                    <FaBullhorn size={48} />
                    <p>{t('legacy.please_select_team_noticeboard')}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="noticeboard-section">
            <div className="section-header">
                <div className="header-navigation">
                    <BackButton
                        onClick={handleBack}
                        ariaLabel={t('common.actions.back')}
                        style={{ marginRight: '16px' }} // Maintain spacing
                    />
                    <div className="header-title-container">
                        <h2 className="section-title-new">
                            {activeTeam?.name} - <span className="highlight">{t('navigation.noticeboard')}</span>
                        </h2>
                        <button className="create-notice-btn-new" onClick={() => setShowCreateModal(true)}>
                            <PlusIcon className="add-icon" />
                            <span>{t('legacy.add_new_notice')}</span>
                        </button>
                    </div>
                </div>
            </div>

            <div className="notices-container">
                {loading && notices.length === 0 ? (
                    <div className="notice-row">
                        {[...Array(6)].map((_, i) => (
                            <div className="notice-col" key={i}>
                                <NoticeCardSkeleton />
                            </div>
                        ))}
                    </div>
                ) : notices.length > 0 ? (
                    <div className="notice-row">
                        {notices.map((notice, index) => {
                            if (notices.length === index + 1) {
                                return (
                                    <div
                                        className="notice-col"
                                        ref={lastNoticeElementRef}
                                        key={notice.id}
                                        onClick={() => setSelectedNotice(notice)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <NoticeCard
                                            notice={notice}
                                            currentUserId={currentUserId}
                                            isOwner={isOwner}
                                            onEdit={(n) => setEditNotice(n)}
                                            onDelete={(n) => setDeleteNotice(n)}
                                        />
                                    </div>
                                );
                            } else {
                                return (
                                    <div
                                        className="notice-col"
                                        key={notice.id}
                                        onClick={() => setSelectedNotice(notice)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <NoticeCard
                                            notice={notice}
                                            currentUserId={currentUserId}
                                            isOwner={isOwner}
                                            onEdit={(n) => setEditNotice(n)}
                                            onDelete={(n) => setDeleteNotice(n)}
                                        />
                                    </div>
                                );
                            }
                        })}
                        {loadingMore && <div className="loading-more" style={{ width: '100%' }}>{t('legacy.loading_more')}</div>}
                    </div>
                ) : (
                    <div className="empty-state">
                        <FaBullhorn size={48} />
                        <p>{t('legacy.no_notices_yet')}</p>
                    </div>
                )}
            </div>

            {showCreateModal && (
                <CreateNoticeModal
                    onClose={() => setShowCreateModal(false)}
                    teamId={activeTeamId}
                />
            )}

            {editNotice && (
                <CreateNoticeModal
                    onClose={() => setEditNotice(null)}
                    teamId={activeTeamId}
                    notice={editNotice}
                />
            )}

            {selectedNotice && (
                <ViewNoticeModal
                    notice={selectedNotice}
                    onClose={() => setSelectedNotice(null)}
                />
            )}

            <DeleteNoticeModal
                isOpen={Boolean(deleteNotice)}
                onClose={closeDeleteModal}
                onConfirm={handleDeleteConfirm}
                notice={deleteNotice}
                loading={deleteLoading}
            />
        </div>
    );
};

export default NoticeboardSection;
